import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import { createChart, ColorType, LineStyle, CandlestickSeries, LineSeries, HistogramSeries, CrosshairMode } from 'lightweight-charts';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Activity, ChevronDown, ChevronUp } from "lucide-react";
import { getPricePrecision } from '../dca/DcaUtils';
import { getChartAdapter, Timeframe, CandleData } from '../dca/ChartAdapters';

interface SignalTradingChartProps {
  symbol: string;
  exchange?: string;
  interval?: string;
  isTestnet?: boolean;
  trades?: any[];
  settings?: any;
  theme?: string;
  strategyType?: string;
  position?: any;
}

const TIMEFRAMES: { label: string; value: Timeframe }[] = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '30m', value: '30m' },
  { label: '1h', value: '1h' },
  { label: '4h', value: '4h' },
  { label: '1d', value: '1d' },
  { label: '1w', value: '1w' },
  { label: '1M', value: '1M' },
];

// --- Indicator Helper Functions ---

const calculateZeroLagEMAArray = (data: number[], period: number): number[] => {
  if (data.length === 0) return [];
  const ema1 = calculateEMAArray(data, period);
  const ema2 = calculateEMAArray(ema1, period);

  const zeroLag = [];
  for (let i = 0; i < ema1.length; i++) {
    zeroLag.push(2 * ema1[i] - ema2[i]);
  }
  return zeroLag;
};

const calculateWMA = (prices: number[], period: number): number | null => {
  if (prices.length < period) return null;
  let sum = 0;
  let weightSum = 0;
  for (let i = 0; i < period; i++) {
    const weight = i + 1;
    sum += prices[prices.length - period + i] * weight;
    weightSum += weight;
  }
  return sum / weightSum;
};

const calculateHMAArray = (prices: number[], period: number): number[] => {
  const halfLen = Math.floor(period / 2);
  const sqrtLen = Math.floor(Math.sqrt(period));

  const rawValues: number[] = [];
  for (let i = 0; i < prices.length; i++) {
    const subset = prices.slice(0, i + 1);
    const wmaHalf = calculateWMA(subset, halfLen);
    const wmaFull = calculateWMA(subset, period);
    if (wmaHalf === null || wmaFull === null) {
      rawValues.push(prices[i]);
    } else {
      rawValues.push(2 * wmaHalf - wmaFull);
    }
  }

  const hma: number[] = [];
  for (let i = 0; i < rawValues.length; i++) {
    const val = calculateWMA(rawValues.slice(0, i + 1), sqrtLen);
    hma.push(val || rawValues[i]);
  }
  return hma;
};

const calculateKEWMA = (data: CandleData[], length: number = 45) => {
  const prices = data.map(d => d.close);
  const n = prices.length;
  if (n < length) return { wma: [], ewma: [], trend: [] };

  const a = 2.0 / (length + 1);
  const n1 = Math.round(length / 2.0);

  let xhat = prices[0];
  let p = 1.0;
  const q = 0.0005;
  const r = 0.097;

  const kalmanValues: number[] = [];
  const wmaValues: (number | null)[] = [];
  const ewmaValues: (number | null)[] = [];
  const trendValues: number[] = [];

  const wma = (values: number[], period: number) => {
    if (values.length < period) return null;
    const window = values.slice(-period);
    let norm = 0;
    let sum = 0;
    for (let i = 0; i < period; i++) {
      const weight = i + 1;
      sum += window[i] * weight;
      norm += weight;
    }
    return sum / norm;
  };

  for (let i = 0; i < n; i++) {
    const price = prices[i];
    const xhatPrior = xhat;
    const pPrior = p + q;
    const k = pPrior / (pPrior + r);
    xhat = xhatPrior + k * (price - xhatPrior);
    p = (1 - k) * pPrior;
    kalmanValues.push(xhat);

    if (i < length - 1) {
      wmaValues.push(null);
      ewmaValues.push(null);
      trendValues.push(0);
      continue;
    }

    const kalmanWindow = kalmanValues.slice(i - length + 1, i + 1);
    const wmaShort = wma(kalmanWindow, n1);
    const wmaLong = wma(kalmanWindow, length);
    if (wmaShort === null || wmaLong === null) {
      wmaValues.push(null);
      ewmaValues.push(null);
      trendValues.push(0);
      continue;
    }

    const currentWma = wmaShort * 2 - wmaLong;
    wmaValues.push(currentWma);

    let currentEwma = currentWma;
    if (i === length - 1) {
      currentEwma = currentWma;
    } else {
      const prevWma = wmaValues[i - 1]!;
      const prevEwma = ewmaValues[i - 1]!;
      let temp = currentWma * a + prevWma * (1 - a);
      temp = temp * a + prevWma * (1 - a);
      temp = temp * a + prevWma * (1 - a);
      currentEwma = temp;
    }
    ewmaValues.push(currentEwma);

    let trend = 0;
    if (i > length - 1) {
      const prevWma = wmaValues[i - 1];
      const prevEwma = ewmaValues[i - 1];
      const prevTrend = trendValues[i - 1] || 0;

      if (prevWma !== null && prevEwma !== null) {
        const crossover = prevWma <= prevEwma && currentWma > currentEwma;
        const crossunder = prevWma >= prevEwma && currentWma < currentEwma;
        if (crossover && !crossunder) trend = 1;
        else if (crossunder) trend = -1;
        else trend = prevTrend;
      } else {
        trend = prevTrend;
      }
    } else {
      trend = 0;
    }
    trendValues.push(trend);
  }

  return { wma: wmaValues, ewma: ewmaValues, trend: trendValues };
};

const calculateEMAArray = (data: number[], period: number): number[] => {
  if (data.length === 0) return [];
  const k = 2 / (period + 1);
  const ema = [data[0]];
  for (let i = 1; i < data.length; i++) {
    ema.push(data[i] * k + ema[i - 1] * (1 - k));
  }
  return ema;
};

const calculateBB = (data: CandleData[], period: number = 20, multiplier: number = 2) => {
  if (data.length < period) return { upper: [], middle: [], lower: [] };
  const upper = [];
  const middle = [];
  const lower = [];

  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      middle.push({ time: data[i].time, value: data[i].close });
      upper.push({ time: data[i].time, value: data[i].close });
      lower.push({ time: data[i].time, value: data[i].close });
      continue;
    }
    const slice = data.slice(i - period + 1, i + 1);
    const sum = slice.reduce((acc, d) => acc + d.close, 0);
    const avg = sum / period;
    const variance = slice.reduce((acc, d) => acc + Math.pow(d.close - avg, 2), 0) / period;
    const stdDev = Math.sqrt(variance);

    middle.push({ time: data[i].time, value: avg });
    upper.push({ time: data[i].time, value: avg + multiplier * stdDev });
    lower.push({ time: data[i].time, value: avg - multiplier * stdDev });
  }
  return { upper, middle, lower };
};

const calculateRSI = (data: CandleData[], period: number = 14) => {
  if (data.length <= period) return [];
  const rsiData = [];
  let gains = 0;
  let losses = 0;

  for (let i = 1; i <= period; i++) {
    const diff = data[i].close - data[i - 1].close;
    if (diff >= 0) gains += diff;
    else losses -= diff;
  }

  let avgGain = gains / period;
  let avgLoss = losses / period;

  for (let i = period; i < data.length; i++) {
    if (i > period) {
      const diff = data[i].close - data[i - 1].close;
      const currentGain = diff >= 0 ? diff : 0;
      const currentLoss = diff < 0 ? -diff : 0;
      avgGain = (avgGain * (period - 1) + currentGain) / period;
      avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
    }
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    const rsi = 100 - (100 / (1 + rs));
    rsiData.push({ time: data[i].time, value: rsi });
  }
  return rsiData;
};

const calculateStochastic = (data: CandleData[], kPeriod: number = 20, dPeriod: number = 12, slowing: number = 12) => {
  const times = data.map(d => d.time);
  
  // 1. Fast %K
  const rawK: (number | null)[] = [];
  for (let i = 0; i < data.length; i++) {
    if (i < kPeriod - 1) {
      rawK.push(null);
      continue;
    }
    const subset = data.slice(i - kPeriod + 1, i + 1);
    const low = Math.min(...subset.map(d => d.low));
    const high = Math.max(...subset.map(d => d.high));
    const denom = high - low;
    if (denom === 0) {
      rawK.push(50);
    } else {
      rawK.push(((data[i].close - low) / denom) * 100);
    }
  }

  // 2. Smoothed K
  const smoothedK: (number | null)[] = [];
  const kValues: { time: any; value: number | null }[] = [];
  for (let i = 0; i < rawK.length; i++) {
    const val = rawK[i];
    if (i < slowing - 1 || val === null) {
      smoothedK.push(null);
      kValues.push({ time: times[i], value: null });
      continue;
    }
    const window = rawK.slice(i - slowing + 1, i + 1).filter((v): v is number => v !== null);
    if (window.length < slowing) {
      smoothedK.push(null);
      kValues.push({ time: times[i], value: null });
    } else {
      const sum = window.reduce((acc, v) => acc + v, 0);
      const kVal = sum / window.length;
      smoothedK.push(kVal);
      kValues.push({ time: times[i], value: kVal });
    }
  }

  // 3. D Values
  const dValues: { time: any; value: number | null }[] = [];
  for (let i = 0; i < smoothedK.length; i++) {
    const val = smoothedK[i];
    if (i < dPeriod - 1 || val === null) {
      dValues.push({ time: times[i], value: null });
      continue;
    }
    const window = smoothedK.slice(i - dPeriod + 1, i + 1).filter((v): v is number => v !== null);
    if (window.length < dPeriod) {
      dValues.push({ time: times[i], value: null });
    } else {
      const sum = window.reduce((acc, v) => acc + v, 0);
      dValues.push({ time: times[i], value: sum / window.length });
    }
  }

  return {
    k: kValues.filter((v): v is { time: any; value: number } => v.value !== null),
    d: dValues.filter((v): v is { time: any; value: number } => v.value !== null)
  };
};

const calculateStochasticRSI = (data: CandleData[], rsiPeriod: number = 14, stochPeriod: number = 14, kPeriod: number = 3, dPeriod: number = 3) => {
  const rsi = calculateRSI(data, rsiPeriod);
  if (rsi.length < stochPeriod + kPeriod + dPeriod) {
    return { k: [], d: [] };
  }

  const times = rsi.map(r => r.time);
  const rsiValues = rsi.map(r => r.value);
  const rawK: number[] = [];

  for (let i = 0; i < rsiValues.length; i++) {
    if (i < stochPeriod - 1) {
      rawK.push(50);
      continue;
    }
    const subset = rsiValues.slice(i - stochPeriod + 1, i + 1);
    const minRsi = Math.min(...subset);
    const maxRsi = Math.max(...subset);
    const denom = maxRsi - minRsi;
    const k = denom === 0 ? 50 : ((rsiValues[i] - minRsi) / denom) * 100;
    rawK.push(k);
  }

  const kValues: { time: any; value: number }[] = [];
  const smoothedK: number[] = [];
  for (let i = 0; i < rawK.length; i++) {
    if (i < kPeriod - 1) {
      smoothedK.push(50);
      kValues.push({ time: times[i], value: 50 });
      continue;
    }
    const sum = rawK.slice(i - kPeriod + 1, i + 1).reduce((acc, v) => acc + v, 0);
    const kVal = sum / kPeriod;
    smoothedK.push(kVal);
    kValues.push({ time: times[i], value: kVal });
  }

  const dValues: { time: any; value: number }[] = [];
  for (let i = 0; i < smoothedK.length; i++) {
    if (i < dPeriod - 1) {
      dValues.push({ time: times[i], value: 50 });
      continue;
    }
    const sum = smoothedK.slice(i - dPeriod + 1, i + 1).reduce((acc, v) => acc + v, 0);
    dValues.push({ time: times[i], value: sum / dPeriod });
  }

  return { k: kValues, d: dValues };
};

const resampleCandles = (data: CandleData[], factor: number): CandleData[] => {
  if (factor <= 1) return data;
  const resampled: CandleData[] = [];
  for (let i = 0; i < data.length; i += factor) {
    const slice = data.slice(i, i + factor);
    if (slice.length === 0) continue;
    const first = slice[0];
    const last = slice[slice.length - 1];
    resampled.push({
      time: first.time,
      open: first.open,
      high: Math.max(...slice.map(d => d.high)),
      low: Math.min(...slice.map(d => d.low)),
      close: last.close,
      volume: slice.reduce((acc, d) => acc + d.volume, 0),
    });
  }
  return resampled;
};

const calculateLinearRegressionChannel = (data: CandleData[], period: number = 50, factor: number = 1) => {
  if (data.length < period * factor) return { upper: [], middle: [], lower: [] };

  // 1. Resample if factor > 1
  let calculationData = data;
  if (factor > 1) {
    calculationData = resampleCandles(data, factor);
  }

  // 2. Calculate LR on calculationData (either LTF or HTF)
  const subset = calculationData.slice(-period);
  const closes = subset.map(d => d.close);
  const n = period;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;

  for (let i = 0; i < n; i++) {
    const x = i;
    const y = closes[i];
    sumX += x; sumY += y; sumXY += (x * y); sumXX += (x * x);
  }

  const denominator = (n * sumXX - Math.pow(sumX, 2));
  if (denominator === 0) return { upper: [], middle: [], lower: [] };

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  let sqDiffSum = 0;
  for (let i = 0; i < n; i++) {
    const x = i;
    const y = closes[i];
    const expectedY = slope * x + intercept;
    sqDiffSum += Math.pow(y - expectedY, 2);
  }
  const stdDev = Math.sqrt(sqDiffSum / n);

  const upper = [];
  const middle = [];
  const lower = [];

  // 3. Render on the original 'data' candles for smooth lines
  const totalLtfCount = period * factor;
  const ltfSubset = data.slice(-totalLtfCount);

  for (let i = 0; i < ltfSubset.length; i++) {
    // Convert LTF index to HTF-scaled index for the LR line
    // i=0 is (totalLtfCount) bars ago. In HTF terms, it is 0.
    // i=totalLtfCount-1 is the latest. In HTF terms, it is (period-1).
    const htfX = i / factor;
    const lrValue = slope * htfX + intercept;
    const time = ltfSubset[i].time;
    middle.push({ time, value: lrValue });
    upper.push({ time, value: lrValue + (2.0 * stdDev) });
    lower.push({ time, value: lrValue - (2.0 * stdDev) });
  }

  return { upper, middle, lower };
};

const calculateSupertrend = (data: CandleData[], period: number = 10, multiplier: number = 3) => {
  if (data.length < period) return [];

  const atr: number[] = [];
  const tr: number[] = [];

  // 1. ATR 계산
  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      tr.push(data[i].high - data[i].low);
    } else {
      const hl = data[i].high - data[i].low;
      const hpc = Math.abs(data[i].high - data[i - 1].close);
      const lpc = Math.abs(data[i].low - data[i - 1].close);
      tr.push(Math.max(hl, hpc, lpc));
    }
  }

  let sumTr = 0;
  for (let i = 0; i < data.length; i++) {
    if (i < period) {
      sumTr += tr[i];
      atr.push(sumTr / (i + 1));
    } else {
      const val = (atr[i - 1] * (period - 1) + tr[i]) / period;
      atr.push(val);
    }
  }

  const supertrend = [];
  let prevUpper = 0;
  let prevLower = 0;
  let prevSuper = 0;
  let trend = 1; // 1: Up, -1: Down

  for (let i = 0; i < data.length; i++) {
    if (i < period) {
      supertrend.push({ time: data[i].time, value: data[i].close, color: '#22c55e' });
      continue;
    }

    const hl2 = (data[i].high + data[i].low) / 2;
    const basicUpper = hl2 + multiplier * atr[i];
    const basicLower = hl2 - multiplier * atr[i];

    // Final Bands
    const finalUpper = (basicUpper < prevUpper || data[i - 1].close > prevUpper) ? basicUpper : prevUpper;
    const finalLower = (basicLower > prevLower || data[i - 1].close < prevLower) ? basicLower : prevLower;

    let currentSuper = 0;
    if (prevSuper === prevUpper) {
      currentSuper = data[i].close > finalUpper ? finalLower : finalUpper;
    } else {
      currentSuper = data[i].close < finalLower ? finalUpper : finalLower;
    }

    const color = currentSuper === finalLower ? '#22c55e' : '#ef5350';
    supertrend.push({ time: data[i].time, value: currentSuper, color });

    prevUpper = finalUpper;
    prevLower = finalLower;
    prevSuper = currentSuper;
  }

  return supertrend;
};

const convertToHeikinAshi = (data: CandleData[]): CandleData[] => {
  if (data.length === 0) return [];
  const haData: CandleData[] = [];
  
  // First candle
  const first = data[0];
  let prevOpen = (first.open + first.close) / 2;
  let prevClose = (first.open + first.high + first.low + first.close) / 4;
  
  haData.push({
    time: first.time,
    open: prevOpen,
    close: prevClose,
    high: Math.max(first.high, prevOpen, prevClose),
    low: Math.min(first.low, prevOpen, prevClose),
    volume: first.volume
  });

  for (let i = 1; i < data.length; i++) {
    const c = data[i];
    const haClose = (c.open + c.high + c.low + c.close) / 4;
    const haOpen = (prevOpen + prevClose) / 2;
    const haHigh = Math.max(c.high, haOpen, haClose);
    const haLow = Math.min(c.low, haOpen, haClose);
    
    haData.push({
      time: c.time,
      open: haOpen,
      close: haClose,
      high: haHigh,
      low: haLow,
      volume: c.volume
    });
    
    prevOpen = haOpen;
    prevClose = haClose;
  }
  
  return haData;
};

// --- Component ---

export const SignalTradingChart: React.FC<SignalTradingChartProps> = React.memo(({
  symbol, exchange, interval, trades = [], settings, isTestnet = false, strategyType = 'hull_macd', position
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const candlestickSeriesRef = useRef<any>(null);
  const ema5SeriesRef = useRef<any>(null);
  const ema10SeriesRef = useRef<any>(null);
  const ema20SeriesRef = useRef<any>(null);
  const ema60SeriesRef = useRef<any>(null);
  const ema120SeriesRef = useRef<any>(null);
  const ema240SeriesRef = useRef<any>(null);

  // ZLMazD (Zero-Lag MACD) Refs
  const zlmacdSeriesRef = useRef<any>(null);
  const zlmacdSignalSeriesRef = useRef<any>(null);
  const zlmacdHistSeriesRef = useRef<any>(null);

  // Normal MACD Refs
  const normalMacdSeriesRef = useRef<any>(null);
  const normalMacdSignalSeriesRef = useRef<any>(null);
  const normalMacdHistSeriesRef = useRef<any>(null);

  // Hull MACD Refs
  const hullMacdSeriesRef = useRef<any>(null);
  const hullMacdSignalSeriesRef = useRef<any>(null);
  const hullMacdHistSeriesRef = useRef<any>(null);

  const volumeSeriesRef = useRef<any>(null);
  const rsiSeriesRef = useRef<any>(null);
  const stoch20KSeriesRef = useRef<any>(null);
  const stoch20DSeriesRef = useRef<any>(null);
  const stoch10KSeriesRef = useRef<any>(null);
  const stoch10DSeriesRef = useRef<any>(null);
  const stoch5KSeriesRef = useRef<any>(null);
  const stoch5DSeriesRef = useRef<any>(null);
  const stochRsiKSeriesRef = useRef<any>(null);
  const stochRsiDSeriesRef = useRef<any>(null);
  const bbUpperSeriesRef = useRef<any>(null);
  const bbMiddleSeriesRef = useRef<any>(null);
  const bbLowerSeriesRef = useRef<any>(null);
  const lrUpperSeriesRef = useRef<any>(null);
  const lrMiddleSeriesRef = useRef<any>(null);
  const lrLowerSeriesRef = useRef<any>(null);
  const supertrendSeriesListRef = useRef<any[]>([]);
  const positionPriceLineRef = useRef<any>(null);
  const kewmaSeriesListRef = useRef<any[]>([]);

  const initialTimeframe = (interval as Timeframe) || '5m';
  const [timeframe, setTimeframe] = useState<Timeframe>(initialTimeframe);
  const [useTestnetData, setUseTestnetData] = useState(false);
  const [showHeikinAshi, setShowHeikinAshi] = useState(false);

  const storageKey = `signal_chart_open_${symbol}_${strategyType}`;
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, String(isOpen));
  }, [isOpen, storageKey]);

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [supertrendData, setSupertrendData] = useState<any[]>([]);
  const [kewmaData, setKewmaData] = useState<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Visibility States
  const [visibleEmas, setVisibleEmas] = useState<Record<number, boolean>>({
    5: true, 10: true, 20: true, 60: true, 120: true, 240: true
  });
  const [showBb, setShowBb] = useState(false);
  const [showChannel, setShowChannel] = useState(strategyType === 'channel_trend');
  const [showZlmacd, setShowZlmacd] = useState(strategyType === 'zerolag_macd');
  const [showNormalMacd, setShowNormalMacd] = useState(strategyType === 'macd_ema' || strategyType === 'macd' || (!['zerolag_macd', 'hull_macd'].includes(strategyType || '')));
  const [showHullMacd, setShowHullMacd] = useState(strategyType === 'hull_macd');
  const [showRsi, setShowRsi] = useState(false);
  const [showStoch20, setShowStoch20] = useState(false);
  const [showStoch10, setShowStoch10] = useState(false);
  const [showStoch5, setShowStoch5] = useState(false);
  const [showStochRsi, setShowStochRsi] = useState(false);
  const [showSupertrend, setShowSupertrend] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [showKewma, setShowKewma] = useState(true);

  const chartHeight = useMemo(() => {
    const isMobile = window.innerWidth < 768;
    const base = isMobile ? 220 : 450;
    const panelH = isMobile ? 52 : 80;
    const visiblePanels = [showZlmacd, showNormalMacd, showHullMacd, showRsi, showVolume, showStoch20, showStoch10, showStoch5, showStochRsi].filter(Boolean).length;
    return base + visiblePanels * panelH;
  }, [showZlmacd, showNormalMacd, showHullMacd, showRsi, showVolume, showStoch20, showStoch10, showStoch5, showStochRsi]);

  const chartHeightRef = useRef(chartHeight);
  chartHeightRef.current = chartHeight;

  const precision = useMemo(() => getPricePrecision(symbol), [symbol]);

  const fetchData = useCallback(async (isInitial = false) => {
    if (!symbol) return;
    if (isInitial) setIsInitialLoading(true);
    setIsLoading(true);

    try {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const adapter = getChartAdapter(exchange);
      const formattedData = await adapter.fetchData(symbol, timeframe, useTestnetData, controller.signal);

      if (controller.signal.aborted || !chartRef.current || !candlestickSeriesRef.current) return;

      if (candlestickSeriesRef.current && formattedData.length > 0) {
        const chartData = showHeikinAshi ? convertToHeikinAshi(formattedData) : formattedData;
        try { candlestickSeriesRef.current.setData(chartData); } catch (e) { return; }

        const prices = chartData.map(d => d.close);

        // Volume
        if (volumeSeriesRef.current) {
          volumeSeriesRef.current.setData(chartData.map(d => ({
            time: d.time,
            value: d.volume,
            color: d.close >= d.open ? 'rgba(38, 166, 154, 0.3)' : 'rgba(239, 83, 80, 0.3)'
          })));
        }

        // EMAs
        if (ema5SeriesRef.current) ema5SeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: calculateEMAArray(prices, 5)[i] })));
        if (ema10SeriesRef.current) ema10SeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: calculateEMAArray(prices, 10)[i] })));
        if (ema20SeriesRef.current) ema20SeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: calculateEMAArray(prices, 20)[i] })));
        if (ema60SeriesRef.current) ema60SeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: calculateEMAArray(prices, 60)[i] })));
        if (ema120SeriesRef.current) ema120SeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: calculateEMAArray(prices, 120)[i] })));
        if (ema240SeriesRef.current) ema240SeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: calculateEMAArray(prices, 240)[i] })));

        // BB 데이터 설정
        const bb = calculateBB(chartData);
        if (bbUpperSeriesRef.current) bbUpperSeriesRef.current.setData(bb.upper);
        if (bbMiddleSeriesRef.current) bbMiddleSeriesRef.current.setData(bb.middle);
        if (bbLowerSeriesRef.current) bbLowerSeriesRef.current.setData(bb.lower);

        // LR Channel 데이터 설정
        const lrPeriod = settings?.lr_period || 50;
        const resampleFactor = settings?.resample_factor || 1;
        const lr = calculateLinearRegressionChannel(chartData, lrPeriod, resampleFactor);
        if (lrUpperSeriesRef.current) lrUpperSeriesRef.current.setData(lr.upper);
        if (lrMiddleSeriesRef.current) lrMiddleSeriesRef.current.setData(lr.middle);
        if (lrLowerSeriesRef.current) lrLowerSeriesRef.current.setData(lr.lower);

        // RSI 데이터 설정
        const rsiData = calculateRSI(chartData);
        if (rsiSeriesRef.current) rsiSeriesRef.current.setData(rsiData);

        // Stochastic 20,10,10 데이터 설정
        const stoch20 = calculateStochastic(chartData, 20, 12, 12);
        if (stoch20KSeriesRef.current) stoch20KSeriesRef.current.setData(stoch20.k);
        if (stoch20DSeriesRef.current) stoch20DSeriesRef.current.setData(stoch20.d);

        // Stochastic 10,6,6 데이터 설정
        const stoch10 = calculateStochastic(chartData, 10, 6, 6);
        if (stoch10KSeriesRef.current) stoch10KSeriesRef.current.setData(stoch10.k);
        if (stoch10DSeriesRef.current) stoch10DSeriesRef.current.setData(stoch10.d);

        // Stochastic 5,3,3 데이터 설정
        const stoch5 = calculateStochastic(chartData, 5, 3, 3);
        if (stoch5KSeriesRef.current) stoch5KSeriesRef.current.setData(stoch5.k);
        if (stoch5DSeriesRef.current) stoch5DSeriesRef.current.setData(stoch5.d);

        // Stochastic RSI 데이터 설정 (기본값: rsi 14, stoch 14, k 3, d 3)
        const stochRsi = calculateStochasticRSI(chartData, 14, 14, 3, 3);
        if (stochRsiKSeriesRef.current) stochRsiKSeriesRef.current.setData(stochRsi.k);
        if (stochRsiDSeriesRef.current) stochRsiDSeriesRef.current.setData(stochRsi.d);

        // KEWMA 데이터 설정
        const kewmaResult = calculateKEWMA(chartData, 45);
        const kData: any[] = [];

        for (let i = 0; i < chartData.length; i++) {
          const w = kewmaResult.wma[i];
          const t = kewmaResult.trend[i];
          const time = chartData[i].time;

          if (w === null) continue;

          // 롱(상승): 청록색(#00bcd4), 숏(하락): 자주색(#d946ef)
          const color = t === -1 ? '#d946ef' : '#00bcd4';
          kData.push({ time, value: w, color });
        }
        setKewmaData(kData);

        // Supertrend 데이터 설정
        const stData = calculateSupertrend(chartData);
        setSupertrendData(stData);

        // MACD Parameters
        const fastLen = settings?.fast_length || 12;
        const slowLen = settings?.slow_length || 26;
        const sigLen = settings?.signal_length || 9;

        // 1. ZLMazD (Zero-Lag MACD)
        const zlFast = calculateZeroLagEMAArray(prices, fastLen);
        const zlSlow = calculateZeroLagEMAArray(prices, slowLen);
        const zlMacdLine = zlFast.map((f, i) => f - zlSlow[i]);
        const zlSignalLine = calculateZeroLagEMAArray(zlMacdLine, sigLen);

        // 2. Normal MACD (EMA)
        const normalFast = calculateEMAArray(prices, fastLen);
        const normalSlow = calculateEMAArray(prices, slowLen);
        const normalMacdLine = normalFast.map((f, i) => f - normalSlow[i]);
        const normalSignalLine = calculateEMAArray(normalMacdLine, sigLen);

        // 3. Hull MACD (HMA)
        const hmaFast = calculateHMAArray(prices, fastLen);
        const hmaSlow = calculateHMAArray(prices, slowLen);
        const hullMacdLine = hmaFast.map((f, i) => f - hmaSlow[i]);
        const hullSignalLine = calculateEMAArray(hullMacdLine, sigLen);

        // Bind ZLMazD Data
        if (zlmacdSeriesRef.current) zlmacdSeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: zlMacdLine[i] })));
        if (zlmacdSignalSeriesRef.current) zlmacdSignalSeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: zlSignalLine[i] })));
        if (zlmacdHistSeriesRef.current) {
          zlmacdHistSeriesRef.current.setData(chartData.map((d, i) => {
            const val = zlMacdLine[i] - zlSignalLine[i];
            return {
              time: d.time,
              value: val,
              color: val >= 0 ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
            };
          }));
        }

        // Bind Normal MACD Data
        if (normalMacdSeriesRef.current) normalMacdSeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: normalMacdLine[i] })));
        if (normalMacdSignalSeriesRef.current) normalMacdSignalSeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: normalSignalLine[i] })));
        if (normalMacdHistSeriesRef.current) {
          normalMacdHistSeriesRef.current.setData(chartData.map((d, i) => {
            const val = normalMacdLine[i] - normalSignalLine[i];
            return {
              time: d.time,
              value: val,
              color: val >= 0 ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
            };
          }));
        }

        // Bind Hull MACD Data
        if (hullMacdSeriesRef.current) hullMacdSeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: hullMacdLine[i] })));
        if (hullMacdSignalSeriesRef.current) hullMacdSignalSeriesRef.current.setData(chartData.map((d, i) => ({ time: d.time, value: hullSignalLine[i] })));
        if (hullMacdHistSeriesRef.current) {
          hullMacdHistSeriesRef.current.setData(chartData.map((d, i) => {
            const val = hullMacdLine[i] - hullSignalLine[i];
            return {
              time: d.time,
              value: val,
              color: val >= 0 ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
            };
          }));
        }

        if (isInitial && chartRef.current) {
          const timeScale = chartRef.current.timeScale();
          const savedBars = localStorage.getItem('signal_chart_bars_to_show');
          const barsToShow = savedBars ? parseInt(savedBars, 10) : 80;
          const totalBars = chartData.length;
          if (totalBars > barsToShow) {
            timeScale.setVisibleLogicalRange({ from: totalBars - barsToShow, to: totalBars + 5 });
          } else {
            timeScale.fitContent();
          }
        }
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') console.error('Failed to fetch signal chart data:', error);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [symbol, timeframe, exchange, settings, useTestnetData, showHeikinAshi]);

  const updateVisibilityAndMargins = useCallback(() => {
    if (!chartRef.current) return;
    try {
      if (ema5SeriesRef.current) {
        ema5SeriesRef.current.applyOptions({ visible: visibleEmas[5] });
        ema10SeriesRef.current.applyOptions({ visible: visibleEmas[10] });
        ema20SeriesRef.current.applyOptions({ visible: visibleEmas[20] });
        if (ema60SeriesRef.current) ema60SeriesRef.current.applyOptions({ visible: visibleEmas[60] });
        ema120SeriesRef.current.applyOptions({ visible: visibleEmas[120] });
        ema240SeriesRef.current.applyOptions({ visible: visibleEmas[240] });
      }
      if (bbUpperSeriesRef.current) {
        bbUpperSeriesRef.current.applyOptions({ visible: showBb });
        bbMiddleSeriesRef.current.applyOptions({ visible: showBb });
        bbLowerSeriesRef.current.applyOptions({ visible: showBb });
      }
      if (lrUpperSeriesRef.current) {
        lrUpperSeriesRef.current.applyOptions({ visible: showChannel });
        lrMiddleSeriesRef.current.applyOptions({ visible: showChannel });
        lrLowerSeriesRef.current.applyOptions({ visible: showChannel });
      }
      if (kewmaSeriesListRef.current) {
        kewmaSeriesListRef.current.forEach(series => {
          try { series.applyOptions({ visible: showKewma }); } catch (e) { }
        });
      }
      if (zlmacdSeriesRef.current) {
        zlmacdSeriesRef.current.applyOptions({ visible: showZlmacd });
        zlmacdSignalSeriesRef.current.applyOptions({ visible: showZlmacd });
        zlmacdHistSeriesRef.current.applyOptions({ visible: showZlmacd });
      }
      if (normalMacdSeriesRef.current) {
        normalMacdSeriesRef.current.applyOptions({ visible: showNormalMacd });
        normalMacdSignalSeriesRef.current.applyOptions({ visible: showNormalMacd });
        normalMacdHistSeriesRef.current.applyOptions({ visible: showNormalMacd });
      }
      if (hullMacdSeriesRef.current) {
        hullMacdSeriesRef.current.applyOptions({ visible: showHullMacd });
        hullMacdSignalSeriesRef.current.applyOptions({ visible: showHullMacd });
        hullMacdHistSeriesRef.current.applyOptions({ visible: showHullMacd });
      }
      if (rsiSeriesRef.current) rsiSeriesRef.current.applyOptions({ visible: showRsi });
      if (stoch20KSeriesRef.current) {
        stoch20KSeriesRef.current.applyOptions({ visible: showStoch20 });
        stoch20DSeriesRef.current.applyOptions({ visible: showStoch20 });
      }
      if (stoch10KSeriesRef.current) {
        stoch10KSeriesRef.current.applyOptions({ visible: showStoch10 });
        stoch10DSeriesRef.current.applyOptions({ visible: showStoch10 });
      }
      if (stoch5KSeriesRef.current) {
        stoch5KSeriesRef.current.applyOptions({ visible: showStoch5 });
        stoch5DSeriesRef.current.applyOptions({ visible: showStoch5 });
      }
      if (stochRsiKSeriesRef.current) {
        stochRsiKSeriesRef.current.applyOptions({ visible: showStochRsi });
        stochRsiDSeriesRef.current.applyOptions({ visible: showStochRsi });
      }
      if (volumeSeriesRef.current) volumeSeriesRef.current.applyOptions({ visible: showVolume });
    } catch (e) { return; }

    // Adjust scale margins if Macd/RSI visibility changes
    if (chartRef.current) {
      const activePanels: { id: string; show: boolean; height: number }[] = [
        { id: 'zlmacd', show: showZlmacd, height: 0.20 },
        { id: 'normal_macd', show: showNormalMacd, height: 0.20 },
        { id: 'hull_macd', show: showHullMacd, height: 0.20 },
        { id: 'rsi', show: showRsi, height: 0.18 },
        { id: 'stoch20', show: showStoch20, height: 0.18 },
        { id: 'stoch10', show: showStoch10, height: 0.18 },
        { id: 'stoch5', show: showStoch5, height: 0.18 },
        { id: 'stochrsi', show: showStochRsi, height: 0.18 }
      ].filter(p => p.show);

      const totalIndicatorHeight = activePanels.reduce((sum, p) => sum + p.height, 0);
      const MAX_INDICATORS_HEIGHT = 0.45;
      let scaledIndicatorHeight = totalIndicatorHeight;
      let scaleFactor = 1.0;
      if (totalIndicatorHeight > MAX_INDICATORS_HEIGHT) {
        scaleFactor = MAX_INDICATORS_HEIGHT / totalIndicatorHeight;
        scaledIndicatorHeight = MAX_INDICATORS_HEIGHT;
      }

      const mainBottom = Math.max(0.1, scaledIndicatorHeight + 0.05);
      chartRef.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.05, bottom: mainBottom }
      });

      let currentTop = 1.0 - scaledIndicatorHeight - 0.02;
      activePanels.forEach((panel) => {
        const panelHeight = panel.height * scaleFactor;
        const top = Math.max(0.01, Math.min(0.98, currentTop));
        const bottom = Math.max(0.01, Math.min(0.98, 1.0 - (currentTop + panelHeight - 0.02)));

        let finalTop = top;
        let finalBottom = bottom;
        if (finalTop + finalBottom >= 1.0) {
          const sum = finalTop + finalBottom;
          finalTop = (finalTop / sum) * 0.98;
          finalBottom = (finalBottom / sum) * 0.98;
        }

        chartRef.current.priceScale(panel.id).applyOptions({
          scaleMargins: { top: finalTop, bottom: finalBottom },
          borderVisible: false
        });

        currentTop += panelHeight;
      });
    }
  }, [visibleEmas, showBb, showChannel, showZlmacd, showNormalMacd, showHullMacd, showRsi, showStoch20, showStoch10, showStoch5, showStochRsi, showSupertrend, showVolume, showKewma]);

  // Update chart height when indicator visibility changes
  useEffect(() => {
    if (chartRef.current && chartContainerRef.current) {
      chartRef.current.applyOptions({ height: chartHeight });
    }
  }, [chartHeight]);

  // Update Visibility on Toggle
  useEffect(() => {
    updateVisibilityAndMargins();
  }, [visibleEmas, showBb, showChannel, showZlmacd, showNormalMacd, showHullMacd, showRsi, showStoch20, showStoch10, showStoch5, showStochRsi, showSupertrend, showVolume, showKewma, updateVisibilityAndMargins]);

  useEffect(() => {
    if (isOpen && chartContainerRef.current && !chartRef.current) {
      const isDark = document.documentElement.classList.contains('dark');

      const chart = createChart(chartContainerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: isDark ? '#D1D4DC' : '#131722',
        },
        grid: {
          vertLines: { color: isDark ? 'rgba(70, 70, 70, 0.1)' : 'rgba(230, 230, 230, 0.5)' },
          horzLines: { color: isDark ? 'rgba(70, 70, 70, 0.1)' : 'rgba(230, 230, 230, 0.5)' },
        },
        width: chartContainerRef.current.clientWidth,
        height: chartHeight,
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          tickMarkFormatter: (time: number, tickMarkType: number, locale: string) => {
            const date = new Date(time * 1000);
            const options: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Seoul', hour12: false };

            switch (tickMarkType) {
              case 0: options.year = 'numeric'; break;
              case 1: options.month = 'short'; break;
              case 2: options.day = 'numeric'; break;
              case 3: options.hour = '2-digit'; options.minute = '2-digit'; break;
              case 4: options.hour = '2-digit'; options.minute = '2-digit'; options.second = '2-digit'; break;
              default: options.hour = '2-digit'; options.minute = '2-digit'; break;
            }

            return new Intl.DateTimeFormat('ko-KR', options).format(date);
          },
        },
        localization: {
          timeFormatter: (time: number) => {
            const date = new Date(time * 1000);
            return new Intl.DateTimeFormat('ko-KR', {
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false,
              timeZone: 'Asia/Seoul'
            }).format(date);
          },
        },
        crosshair: { mode: CrosshairMode.Normal },
      });

      // 범례(Legend) 요소 생성 (TradingChart.tsx와 동일한 스타일)
      const legend = document.createElement('div');
      legend.style.position = 'absolute';
      legend.style.left = '12px';
      legend.style.top = '12px';
      legend.style.zIndex = '5';
      legend.style.fontSize = '11px';
      legend.style.fontFamily = 'Inter, sans-serif';
      legend.style.fontWeight = '900';
      legend.style.pointerEvents = 'none';
      legend.style.display = 'flex';
      legend.style.gap = '8px';
      legend.style.color = isDark ? '#D1D4DC' : '#131722';
      chartContainerRef.current?.appendChild(legend);

      const setLegendText = (priceData?: any, cursorPrice?: number, lastPrice?: number) => {
        if (!priceData && !cursorPrice) {
          legend.innerHTML = '';
          return;
        }

        const { open, high, low, close } = priceData || {};
        const f = (n: number) => n.toFixed(precision);

        let html = '';

        // 1. 커서 위치 가격 및 현재가 대비 격차 (%)
        if (cursorPrice && lastPrice) {
          const diff = ((cursorPrice - lastPrice) / lastPrice) * 100;
          const diffColor = diff >= 0 ? '#26a69a' : '#ef5350';
          const diffSign = diff >= 0 ? '+' : '';
          html += `
            <div style="display: flex; gap: 6px; align-items: center; margin-right: 12px; border-right: 1px solid rgba(128,128,128,0.2); padding-right: 12px;">
              <span style="color: ${diffColor}">${f(cursorPrice)}</span>
              <span style="font-size: 10px; color: ${diffColor}; background: ${diffColor}15; padding: 1px 4px; border-radius: 4px;">${diffSign}${diff.toFixed(2)}%</span>
            </div>
          `;
        }

        // 2. 캔들 OHLC 정보 (데이터가 있을 때만)
        if (priceData) {
          const isUp = close >= open;
          const color = isUp ? '#26a69a' : '#ef5350';
          html += `
            <div style="display: flex; gap: 4px;">
              <span style="opacity: 0.5;">O</span><span style="color: ${color}">${f(open)}</span>
              <span style="opacity: 0.5; margin-left: 4px;">H</span><span style="color: ${color}">${f(high)}</span>
              <span style="opacity: 0.5; margin-left: 4px;">L</span><span style="color: ${color}">${f(low)}</span>
              <span style="opacity: 0.5; margin-left: 4px;">C</span><span style="color: ${color}">${f(close)}</span>
            </div>
          `;
        }

        legend.innerHTML = html;
      };

      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a', downColor: '#ef5350', borderVisible: false,
        wickUpColor: '#26a69a', wickDownColor: '#ef5350',
      });

      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceScaleId: 'volume',
        visible: showVolume,
      });
      chart.priceScale('volume').applyOptions({
        scaleMargins: { top: 0.8, bottom: 0 },
      });

      const ema5Series = chart.addSeries(LineSeries, { color: isDark ? '#ffffff' : '#000000', lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false });
      const ema10Series = chart.addSeries(LineSeries, { color: '#22c55e', lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false });
      const ema20Series = chart.addSeries(LineSeries, { color: '#ff0000', lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false });
      const ema60Series = chart.addSeries(LineSeries, { color: '#fda635ff', lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false });
      const ema120Series = chart.addSeries(LineSeries, { color: '#c47814ff', lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false });
      const ema240Series = chart.addSeries(LineSeries, { color: '#2962FF', lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false });

      const bbColor = isDark ? 'rgba(150, 150, 150, 0.4)' : 'rgba(120, 120, 120, 0.4)';
      const bbUpperSeries = chart.addSeries(LineSeries, { color: bbColor, lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false, visible: showBb });
      const bbMiddleSeries = chart.addSeries(LineSeries, { color: bbColor, lineWidth: 1, lineStyle: LineStyle.Dashed, crosshairMarkerVisible: false, priceLineVisible: false, visible: showBb });
      const bbLowerSeries = chart.addSeries(LineSeries, { color: bbColor, lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false, visible: showBb });

      const lrColor = isDark ? 'rgba(41, 98, 255, 0.6)' : 'rgba(0, 82, 204, 0.6)';
      const lrUpperSeries = chart.addSeries(LineSeries, { color: lrColor, lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false, visible: showChannel });
      const lrMiddleSeries = chart.addSeries(LineSeries, { color: lrColor, lineWidth: 1, lineStyle: LineStyle.Dashed, crosshairMarkerVisible: false, priceLineVisible: false, visible: showChannel });
      const lrLowerSeries = chart.addSeries(LineSeries, { color: lrColor, lineWidth: 1, crosshairMarkerVisible: false, priceLineVisible: false, visible: showChannel });

      // 1. ZLMazD (Zero-Lag MACD) Series
      const zlmacdSeries = chart.addSeries(LineSeries, { color: '#2962FF', lineWidth: 1, priceScaleId: 'zlmacd', title: 'ZLMazD', crosshairMarkerVisible: false, visible: showZlmacd });
      const zlmacdSignalSeries = chart.addSeries(LineSeries, { color: '#FF6D00', lineWidth: 1, priceScaleId: 'zlmacd', title: 'Signal', crosshairMarkerVisible: false, visible: showZlmacd });
      const zlmacdHistSeries = chart.addSeries(HistogramSeries, { priceScaleId: 'zlmacd', title: 'Hist', visible: showZlmacd });

      // 2. Normal MACD (EMA) Series
      const normalMacdSeries = chart.addSeries(LineSeries, { color: '#00bcd4', lineWidth: 1, priceScaleId: 'normal_macd', title: 'MACD', crosshairMarkerVisible: false, visible: showNormalMacd });
      const normalMacdSignalSeries = chart.addSeries(LineSeries, { color: '#e040fb', lineWidth: 1, priceScaleId: 'normal_macd', title: 'Signal', crosshairMarkerVisible: false, visible: showNormalMacd });
      const normalMacdHistSeries = chart.addSeries(HistogramSeries, { priceScaleId: 'normal_macd', title: 'Hist', visible: showNormalMacd });

      // 3. Hull MACD (HMA) Series
      const hullMacdSeries = chart.addSeries(LineSeries, { color: '#22c55e', lineWidth: 1, priceScaleId: 'hull_macd', title: 'Hull MACD', crosshairMarkerVisible: false, visible: showHullMacd });
      const hullMacdSignalSeries = chart.addSeries(LineSeries, { color: '#fda635ff', lineWidth: 1, priceScaleId: 'hull_macd', title: 'Signal', crosshairMarkerVisible: false, visible: showHullMacd });
      const hullMacdHistSeries = chart.addSeries(HistogramSeries, { priceScaleId: 'hull_macd', title: 'Hist', visible: showHullMacd });

      const rsiSeries = chart.addSeries(LineSeries, {
        color: '#7c3aed', lineWidth: 1, priceScaleId: 'rsi', title: 'RSI',
        visible: showRsi,
        autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } })
      });
      rsiSeries.createPriceLine({ price: 70, color: 'rgba(239, 83, 80, 0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '70' });
      rsiSeries.createPriceLine({ price: 30, color: 'rgba(38, 166, 154, 0.5)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '30' });

      // Stochastic 20,10,10 Series
      const stoch20KSeries = chart.addSeries(LineSeries, { color: '#22c55e', lineWidth: 2, priceScaleId: 'stoch20', title: 'Stoch20 %K', crosshairMarkerVisible: false, visible: showStoch20, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      const stoch20DSeries = chart.addSeries(LineSeries, { color: '#7c7c7cff', lineWidth: 1, priceScaleId: 'stoch20', title: 'Stoch20 %D', crosshairMarkerVisible: false, visible: showStoch20, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      stoch20KSeries.createPriceLine({ price: 80, color: 'rgba(239, 83, 80, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '80' });
      stoch20KSeries.createPriceLine({ price: 20, color: 'rgba(38, 166, 154, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '20' });

      // Stochastic 10,6,6 Series
      const stoch10KSeries = chart.addSeries(LineSeries, { color: '#444444ff', lineWidth: 2, priceScaleId: 'stoch10', title: 'Stoch10 %K', crosshairMarkerVisible: false, visible: showStoch10, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      const stoch10DSeries = chart.addSeries(LineSeries, { color: '#7c7c7cff', lineWidth: 1, priceScaleId: 'stoch10', title: 'Stoch10 %D', crosshairMarkerVisible: false, visible: showStoch10, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      stoch10KSeries.createPriceLine({ price: 80, color: 'rgba(239, 83, 80, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '80' });
      stoch10KSeries.createPriceLine({ price: 20, color: 'rgba(38, 166, 154, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '20' });

      // Stochastic 5,3,3 Series
      const stoch5KSeries = chart.addSeries(LineSeries, { color: '#E91E63', lineWidth: 2, priceScaleId: 'stoch5', title: 'Stoch5 %K', crosshairMarkerVisible: false, visible: showStoch5, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      const stoch5DSeries = chart.addSeries(LineSeries, { color: '#7c7c7cff', lineWidth: 1, priceScaleId: 'stoch5', title: 'Stoch5 %D', crosshairMarkerVisible: false, visible: showStoch5, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      stoch5KSeries.createPriceLine({ price: 80, color: 'rgba(239, 83, 80, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '80' });
      stoch5KSeries.createPriceLine({ price: 20, color: 'rgba(38, 166, 154, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '20' });

      // Stochastic RSI Series
      const stochRsiKSeries = chart.addSeries(LineSeries, { color: '#3F51B5', lineWidth: 1, priceScaleId: 'stochrsi', title: 'StochRsi %K', crosshairMarkerVisible: false, visible: showStochRsi, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      const stochRsiDSeries = chart.addSeries(LineSeries, { color: '#FF5722', lineWidth: 1, priceScaleId: 'stochrsi', title: 'StochRsi %D', crosshairMarkerVisible: false, visible: showStochRsi, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
      stochRsiKSeries.createPriceLine({ price: 80, color: 'rgba(239, 83, 80, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '80' });
      stochRsiKSeries.createPriceLine({ price: 20, color: 'rgba(38, 166, 154, 0.4)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '20' });

      chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.1, bottom: 0.35 } });
      chart.priceScale('zlmacd').applyOptions({ borderVisible: false });
      chart.priceScale('normal_macd').applyOptions({ borderVisible: false });
      chart.priceScale('hull_macd').applyOptions({ borderVisible: false });
      chart.priceScale('rsi').applyOptions({ scaleMargins: { top: 0.85, bottom: 0.05 }, borderVisible: false });
      chart.priceScale('stoch20').applyOptions({ borderVisible: false });
      chart.priceScale('stoch10').applyOptions({ borderVisible: false });
      chart.priceScale('stoch5').applyOptions({ borderVisible: false });
      chart.priceScale('stochrsi').applyOptions({ borderVisible: false });

      candlestickSeriesRef.current = candlestickSeries;
      volumeSeriesRef.current = volumeSeries;
      ema5SeriesRef.current = ema5Series;
      ema10SeriesRef.current = ema10Series;
      ema20SeriesRef.current = ema20Series;
      ema60SeriesRef.current = ema60Series;
      ema120SeriesRef.current = ema120Series;
      ema240SeriesRef.current = ema240Series;
      bbUpperSeriesRef.current = bbUpperSeries;
      bbMiddleSeriesRef.current = bbMiddleSeries;
      bbLowerSeriesRef.current = bbLowerSeries;
      lrUpperSeriesRef.current = lrUpperSeries;
      lrMiddleSeriesRef.current = lrMiddleSeries;
      lrLowerSeriesRef.current = lrLowerSeries;

      zlmacdSeriesRef.current = zlmacdSeries;
      zlmacdSignalSeriesRef.current = zlmacdSignalSeries;
      zlmacdHistSeriesRef.current = zlmacdHistSeries;

      normalMacdSeriesRef.current = normalMacdSeries;
      normalMacdSignalSeriesRef.current = normalMacdSignalSeries;
      normalMacdHistSeriesRef.current = normalMacdHistSeries;

      hullMacdSeriesRef.current = hullMacdSeries;
      hullMacdSignalSeriesRef.current = hullMacdSignalSeries;
      hullMacdHistSeriesRef.current = hullMacdHistSeries;

      rsiSeriesRef.current = rsiSeries;
      stoch20KSeriesRef.current = stoch20KSeries;
      stoch20DSeriesRef.current = stoch20DSeries;
      stoch10KSeriesRef.current = stoch10KSeries;
      stoch10DSeriesRef.current = stoch10DSeries;
      stoch5KSeriesRef.current = stoch5KSeries;
      stoch5DSeriesRef.current = stoch5DSeries;
      stochRsiKSeriesRef.current = stochRsiKSeries;
      stochRsiDSeriesRef.current = stochRsiDSeries;
      chartRef.current = chart;

      // 줌(캔들 수) 상태를 저장하여 다른 코인 선택 시에도 동일 줌 유지
      chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
        const range = chart.timeScale().getVisibleLogicalRange();
        if (range) {
          const width = Math.round(range.to - range.from);
          if (width > 5 && width < 1000) {
            localStorage.setItem('signal_chart_bars_to_show', String(width));
          }
        }
      });

      // 십자선 이동 시 가격 업데이트
      chart.subscribeCrosshairMove((param) => {
        if (param.point && candlestickSeriesRef.current) {
          const data = param.seriesData.get(candlestickSeriesRef.current);
          const priceAtCursor = candlestickSeriesRef.current.coordinateToPrice(param.point.y);

          // 현재가(가장 최근 데이터의 종가) 가져오기
          let lastPrice = 0;
          try {
            const allData = candlestickSeriesRef.current.data();
            if (allData && allData.length > 0) {
              lastPrice = allData[allData.length - 1].close;
            }
          } catch (e) { }

          setLegendText(data, priceAtCursor, lastPrice);
        } else {
          setLegendText(undefined);
        }
      });

      const handleResize = () => {
        if (chartContainerRef.current) {
          chart.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartHeightRef.current,
          });
        }
      };
      window.addEventListener('resize', handleResize);
      updateVisibilityAndMargins();
      fetchData(true);
      return () => {
        window.removeEventListener('resize', handleResize);
        try { chart.remove(); } catch (e) { }
        if (legend.parentNode) legend.parentNode.removeChild(legend);
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        volumeSeriesRef.current = null;
        ema5SeriesRef.current = null;
        ema10SeriesRef.current = null;
        ema20SeriesRef.current = null;
        ema120SeriesRef.current = null;
        ema240SeriesRef.current = null;
        bbUpperSeriesRef.current = null;
        bbMiddleSeriesRef.current = null;
        bbLowerSeriesRef.current = null;
        lrUpperSeriesRef.current = null;
        lrMiddleSeriesRef.current = null;
        lrLowerSeriesRef.current = null;
        zlmacdSeriesRef.current = null;
        zlmacdSignalSeriesRef.current = null;
        zlmacdHistSeriesRef.current = null;
        normalMacdSeriesRef.current = null;
        normalMacdSignalSeriesRef.current = null;
        normalMacdHistSeriesRef.current = null;
        hullMacdSeriesRef.current = null;
        hullMacdSignalSeriesRef.current = null;
        hullMacdHistSeriesRef.current = null;
        rsiSeriesRef.current = null;
        stoch20KSeriesRef.current = null;
        stoch20DSeriesRef.current = null;
        stoch10KSeriesRef.current = null;
        stoch10DSeriesRef.current = null;
        stoch5KSeriesRef.current = null;
        stoch5DSeriesRef.current = null;
        stochRsiKSeriesRef.current = null;
        stochRsiDSeriesRef.current = null;
        positionPriceLineRef.current = null;
        supertrendSeriesListRef.current = [];
        kewmaSeriesListRef.current = [];
      };
    }
  }, [isOpen, symbol, exchange, fetchData, showKewma, showStoch20, showStoch10, showStoch5, showStochRsi]);

  useEffect(() => {
    const timer = setInterval(() => fetchData(false), 15000);
    return () => clearInterval(timer);
  }, [fetchData]);

  // Trade Markers
  useEffect(() => {
    if (!candlestickSeriesRef.current) return;

    const markers: any[] = (trades || []).map(t => {
      const isEntry = t.type?.startsWith('entry');
      const isLong = t.side === 'long';
      const time = Math.floor(new Date(t.created_at).getTime() / 1000);
      let shape: any = 'arrowUp', position: any = 'belowBar', color = '#26a69a', text = isLong ? 'LONG' : 'SHORT';
      if (isEntry) {
        if (!isLong) { shape = 'arrowDown'; position = 'aboveBar'; color = '#ef5350'; }
      } else {
        shape = 'circle'; position = isLong ? 'aboveBar' : 'belowBar'; color = '#FF9800'; text = 'EXIT';
      }
      return { time, position, color, shape, text };
    }).sort((a, b) => a.time - b.time);


    // Supertrend Markers
    const stMarkers: any[] = [];
    if (showSupertrend && supertrendData.length > 0) {
      // 첫 번째 포인트를 항상 표시
      const first = supertrendData[0];
      const isFirstUp = first.color === '#22c55e';
      stMarkers.push({
        time: first.time,
        position: isFirstUp ? 'belowBar' : 'aboveBar',
        color: isFirstUp ? '#22c55e' : '#ef5350',
        shape: isFirstUp ? 'arrowUp' : 'arrowDown',
        text: isFirstUp ? '★ ST LONG' : '★ ST SHORT',
        size: 2,
      });
      // 이후 색상 변환 시점에 마커 추가
      for (let i = 1; i < supertrendData.length; i++) {
        if (supertrendData[i].color !== supertrendData[i - 1].color) {
          const isUp = supertrendData[i].color === '#22c55e';
          stMarkers.push({
            time: supertrendData[i].time,
            position: isUp ? 'belowBar' : 'aboveBar',
            color: isUp ? '#22c55e' : '#ef5350',
            shape: isUp ? 'arrowUp' : 'arrowDown',
            text: isUp ? '★ ST LONG' : '★ ST SHORT',
            size: 2,
          });
        }
      }
    }

    const allMarkers = [...markers, ...stMarkers].sort((a, b) => {
      const aTime = typeof a.time === 'number' ? a.time : (Date.parse(a.time as string) / 1000);
      const bTime = typeof b.time === 'number' ? b.time : (Date.parse(b.time as string) / 1000);
      return aTime - bTime;
    });

    if (candlestickSeriesRef.current && typeof candlestickSeriesRef.current.setMarkers === 'function') {
      try { candlestickSeriesRef.current.setMarkers(allMarkers); } catch (e) { }
    }
  }, [trades, showSupertrend, supertrendData, isOpen]);

  // Supertrend Segment Rendering (Multi-series for color flipping)
  useEffect(() => {
    if (!chartRef.current) return;

    // Cleanup old segments
    supertrendSeriesListRef.current.forEach(series => {
      try { chartRef.current.removeSeries(series); } catch (e) { }
    });
    supertrendSeriesListRef.current = [];

    if (!showSupertrend || supertrendData.length === 0) return;

    // Build segments by color
    const segments: any[] = [];
    let currentSegment: any[] = [];
    let lastColor = supertrendData[0].color;

    supertrendData.forEach((d, i) => {
      if (d.color !== lastColor) {
        // Close previous segment
        if (currentSegment.length > 0) {
          segments.push({ color: lastColor, data: [...currentSegment] });
        }
        // Start new segment (No overlap to keep segments disconnected)
        currentSegment = [{ time: d.time, value: d.value }];
        lastColor = d.color;
      } else {
        currentSegment.push({ time: d.time, value: d.value });
      }
    });
    if (currentSegment.length > 0) {
      segments.push({ color: lastColor, data: currentSegment });
    }

    // Apply segments to chart
    segments.forEach(seg => {
      const series = chartRef.current.addSeries(LineSeries, {
        color: seg.color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
      series.setData(seg.data);
      supertrendSeriesListRef.current.push(series);
    });

  }, [showSupertrend, supertrendData, isOpen]);

  // KEWMA Segment Rendering (Multi-series for color flipping without cross-chart bridges)
  useEffect(() => {
    if (!chartRef.current) return;

    // Cleanup old segments
    kewmaSeriesListRef.current.forEach(series => {
      try { chartRef.current.removeSeries(series); } catch (e) { }
    });
    kewmaSeriesListRef.current = [];

    if (!showKewma || kewmaData.length === 0) return;

    // Build segments by color
    const segments: any[] = [];
    let currentSegment: any[] = [];
    let lastColor = kewmaData[0].color;

    kewmaData.forEach((d, i) => {
      if (d.color !== lastColor) {
        if (currentSegment.length > 0) {
          segments.push({ color: lastColor, data: [...currentSegment] });
        }
        // 이전 세그먼트의 마지막 포인트를 포함하여 세그먼트 간의 끊어짐(Gap)을 연결
        const prevPoint = i > 0 ? kewmaData[i - 1] : null;
        currentSegment = prevPoint ? [{ time: prevPoint.time, value: prevPoint.value }, { time: d.time, value: d.value }] : [{ time: d.time, value: d.value }];
        lastColor = d.color;
      } else {
        currentSegment.push({ time: d.time, value: d.value });
      }
    });
    if (currentSegment.length > 0) {
      segments.push({ color: lastColor, data: currentSegment });
    }

    // Apply segments to chart
    segments.forEach(seg => {
      const series = chartRef.current.addSeries(LineSeries, {
        color: seg.color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        visible: showKewma,
      });
      series.setData(seg.data);
      kewmaSeriesListRef.current.push(series);
    });

  }, [showKewma, kewmaData, isOpen]);

  // Position Price Line
  useEffect(() => {
    if (!candlestickSeriesRef.current) return;

    // 기존 라인 제거
    if (positionPriceLineRef.current) {
      try { candlestickSeriesRef.current.removePriceLine(positionPriceLineRef.current); } catch (e) { }
      positionPriceLineRef.current = null;
    }

    // 새 라인 추가 (포지션이 있을 경우)
    if (position && position.side && position.entry_price > 0) {
      const color = position.side === 'long' ? '#26a69a' : '#ef5350';
      const label = `${position.side.toUpperCase()} Entry`;

      try {
        positionPriceLineRef.current = candlestickSeriesRef.current.createPriceLine({
          price: Number(position.entry_price),
          color: color,
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: label,
        });
      } catch (e) { }
    }
  }, [position, candlestickSeriesRef.current]);

  return (
    <Card className="rounded-3xl border border-border/50 shadow-md bg-card overflow-hidden">
      <div
        className="flex flex-col md:flex-row md:items-center justify-between p-4 border-b border-border/30 gap-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-xl text-primary"><Activity className="w-4 h-4" /></div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black text-foreground leading-none">{symbol} Signal View</h3>
              <span className="text-[9px] font-black bg-primary/10 text-primary px-1.5 py-0.5 rounded uppercase">
                {strategyType === 'zerolag_macd' ? 'Zero-Lag' : strategyType === 'hull_macd' ? 'Hull MACD' : strategyType}
              </span>
              {isTestnet && (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <span className={`text-[8px] font-bold px-1 py-0.5 rounded leading-none ${useTestnetData ? 'bg-indigo-500/20 text-indigo-500' : 'bg-muted text-muted-foreground'}`}>
                    {useTestnetData ? 'Testnet' : 'Mainnet'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUseTestnetData(!useTestnetData)}
                    className={`h-5 px-1.5 text-[8px] rounded transition-all leading-none ${useTestnetData
                      ? "text-red-600 dark:text-red-400 font-black border-red-500/40"
                      : "text-muted-foreground font-bold border-border"
                      }`}
                  >
                    use testnet
                  </Button>
                </div>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 font-bold uppercase tracking-wider">Advanced Strategy Monitoring</p>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
          {!isOpen && (
            <div className="hidden md:flex items-center gap-2 text-[10px] font-black text-muted-foreground uppercase bg-muted/50 px-3 py-1 rounded-full">
              Click to Expand Chart
            </div>
          )}
          {isOpen && (
            <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {/* Indicators Toggle */}
              <div className="flex flex-wrap gap-1 bg-muted/40 p-1 rounded-xl mr-2">
                {[5, 10, 20, 60, 120, 240].map(period => (
                  <Button
                    key={period}
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisibleEmas(prev => ({ ...prev, [period]: !prev[period] }))}
                    className={`h-7 px-2 text-[9px] font-black rounded-lg ${visibleEmas[period] ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}
                  >
                    {period}
                  </Button>
                ))}
                <div className="w-[1px] h-4 bg-border/50 self-center mx-1" />
                <Button variant="ghost" size="sm" onClick={() => setShowHeikinAshi(!showHeikinAshi)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showHeikinAshi ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>HA</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowVolume(!showVolume)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showVolume ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>VOL</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowBb(!showBb)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showBb ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>BB</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowChannel(!showChannel)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showChannel ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>CH</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowNormalMacd(!showNormalMacd)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showNormalMacd ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>Macd</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowHullMacd(!showHullMacd)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showHullMacd ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>Hull Macd</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowZlmacd(!showZlmacd)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showZlmacd ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>Zl Macd</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowRsi(!showRsi)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showRsi ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>RSI</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowStochRsi(!showStochRsi)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showStochRsi ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>StoRsi</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowStoch20(!showStoch20)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showStoch20 ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>Stoch20</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowStoch10(!showStoch10)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showStoch10 ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>Stoch10</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowStoch5(!showStoch5)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showStoch5 ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>Stoch5</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowSupertrend(!showSupertrend)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showSupertrend ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>ST</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowKewma(!showKewma)} className={`h-7 px-2 text-[9px] font-black rounded-lg ${showKewma ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground'}`}>KEWMA</Button>
              </div>

              <div className="flex gap-1 bg-muted/40 p-1 rounded-xl">
                {TIMEFRAMES.map((tf) => (
                  <button key={tf.value} onClick={() => setTimeframe(tf.value)} className={`px-2 py-1 text-[10px] font-black rounded-lg transition-all ${timeframe === tf.value ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{tf.label}</button>
                ))}
              </div>
              <button onClick={() => fetchData(true)} disabled={isLoading} className={`p-2 rounded-xl hover:bg-muted transition-colors ${isLoading && 'animate-spin opacity-50'}`}><RefreshCw className="w-4 h-4" /></button>
            </div>
          )}
          <div className="bg-muted p-1 rounded-lg">
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="relative p-2" style={{ height: chartHeight }}>
          {isInitialLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/50 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                <p className="text-xs font-black text-muted-foreground animate-pulse">데이터 로드 중...</p>
              </div>
            </div>
          )}
          <div ref={chartContainerRef} className="w-full h-full" />
        </div>
      )}

      {isOpen && (
        <div className="p-3 bg-muted/20 border-t border-border/30 flex justify-center">
          <Button
            onClick={() => setIsOpen(false)}
            variant="ghost"
            size="sm"
            className="w-full max-w-[200px] text-[11px] font-black h-8 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl gap-1"
          >
            <ChevronUp className="w-3.5 h-3.5" /> 차트 닫기
          </Button>
        </div>
      )}
    </Card>
  );
});
